import { cn } from "@/lib/utils";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./button";
import { FilterButton } from "./filter-button";

export interface FilterOption {
  id: string;
  label: string;
  number?: number;
  subNumber?: number;
  hasError?: boolean;
  hasWarning?: boolean;
  tooltip?: string;
}

export interface FilterBarProps {
  options: FilterOption[];
  selectedFilter: string | null;
  onFilterChange: (filterId: string) => void;
  className?: string;
  isLoading?: boolean;
}

export function FilterBar({
  options,
  selectedFilter,
  onFilterChange,
  className,
  isLoading = false,
}: FilterBarProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  const initializeScrollDetection = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollState();

    resizeObserverRef.current = new ResizeObserver(() => {
      updateScrollState();
    });
    resizeObserverRef.current.observe(container);

    let scrollTimeout: number;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(updateScrollState, 150);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [updateScrollState]);

  useEffect(() => {
    const cleanup = initializeScrollDetection();
    return cleanup;
  }, [initializeScrollDetection]);

  const scroll = (direction: "left" | "right") => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollAmount = 200;
    const newScrollLeft =
      direction === "left"
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  };

  return (
    <div className={cn("flex relative items-center", className)}>
      {canScrollLeft && (
        <>
          <div className="absolute top-0 bottom-0 left-0 z-10 w-20 pointer-events-none bg-linear-to-r to-transparent from-background" />
          <Button
            variant="ghost"
            size="sm"
            className="absolute left-0 z-20 w-8 h-8 p-0 border rounded-full shadow-xs backdrop-blur-xs bg-background/80"
            onClick={() => scroll("left")}
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </Button>
        </>
      )}

      <div
        ref={scrollContainerRef}
        className="flex items-center px-2 space-x-3 overflow-x-auto"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {options.map((option) => (
          <div key={option.id} className="flex items-center space-x-1">
            <FilterButton
              isSelected={selectedFilter === option.id}
              onClick={() => onFilterChange(option.id)}
              number={option.number}
              hasError={option.hasError}
              numberLoading={isLoading}
              subNumber={option.subNumber}
              hasWarning={option.hasWarning}
              tooltip={option.tooltip}
              className="relative"
            >
              {option.label}
            </FilterButton>
          </div>
        ))}
      </div>

      {canScrollRight && (
        <>
          <div className="absolute top-0 bottom-0 right-0 z-10 w-20 pointer-events-none bg-linear-to-l to-transparent from-background" />
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-0 z-20 w-8 h-8 p-0 border rounded-full shadow-xs backdrop-blur-xs bg-background/80"
            onClick={() => scroll("right")}
          >
            <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </>
      )}
    </div>
  );
}
